import React, { useState, useEffect } from 'react';
import { 
  User, Baby, Mail, Phone, Calendar, Upload, 
  CheckCircle, AlertCircle, ArrowRight, ArrowLeft, FileText,
  Home, Building2
} from 'lucide-react';
import crecheApi from '../../services/crecheApi';
import './PreInscricaoPublic.css';

export default function PreInscricaoPublicPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [instituicoes, setInstituicoes] = useState([]);
  const [grupos, setGrupos] = useState([]);
    const fileInputRef = React.useRef(null);

  const [formData, setFormData] = useState({
    instituicaoId: '',
    grupoId: '',
    responsavel: {
      nome: '',
      email: '',
      telefone: '',
      parentesco: 'Encarregado'
    },
    crianca: {
      nome: '',
      dataNascimento: '',
      genero: ''
    }
  });

  const [documentos, setDocumentos] = useState([]);

  useEffect(() => {
    carregarInstituicoes();
  }, []);

  useEffect(() => {
    if (formData.instituicaoId) {
      carregarGrupos(formData.instituicaoId);
    }
  }, [formData.instituicaoId]);

  const carregarInstituicoes = async () => {
    try {
      const data = await crecheApi.listarInstituicoes();
      setInstituicoes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar instituições:', err);
    }
  };

  const carregarGrupos = async (instituicaoId) => {
    try {
      const data = await crecheApi.listarGruposPublico(instituicaoId);
      setGrupos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setDocumentos(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setDocumentos(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    setError('');
    
    if (step === 1) {
      if (!formData.instituicaoId) {
        setError('Selecione uma instituição');
        return false;
      }
    }
    
    if (step === 2) {
      const { nome, dataNascimento } = formData.crianca;
      if (!nome || !dataNascimento) {
        setError('Preencha o nome e data de nascimento da criança');
        return false;
      }
    }
    
    if (step === 3) {
      const { nome, email, telefone } = formData.responsavel;
      if (!nome || !email || !telefone) {
        setError('Preencha todos os dados do responsável');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Email inválido');
        return false;
      }
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        instituicaoId: parseInt(formData.instituicaoId),
        grupoId: formData.grupoId ? parseInt(formData.grupoId) : null,
        responsavel: formData.responsavel,
        crianca: formData.crianca
      };

      const response = await crecheApi.criarPreInscricaoPublica(payload);
      
      if (response?.ids?.estudanteId) {
        if (documentos.length > 0) {
          try {
            await crecheApi.uploadDocumentosPreInscricao(
              response.ids.estudanteId,
              documentos
            );
          } catch (err) {
            console.warn('Erro ao enviar documentos:', err);
          }
        }
        
        setSuccess(true);
        setStep(5);
      }
    } catch (err) {
      setError(err.response?.data?.erro || 'Erro ao submeter pré-inscrição');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <div className="form-group">
        <label className="form-label">
          <Building2 size={18} />
          Instituição *
        </label>
        <select
          value={formData.instituicaoId}
          onChange={(e) => setFormData(prev => ({ ...prev, instituicaoId: e.target.value }))}
          className="form-select"
        >
          <option value="">Selecione uma instituição</option>
          {instituicoes.map(inst => (
            <option key={inst.id} value={inst.id}>{inst.nome}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">
          Grupo / Turma (Opcional)
        </label>
        <select
          value={formData.grupoId}
          onChange={(e) => setFormData(prev => ({ ...prev, grupoId: e.target.value }))}
          className="form-select"
          disabled={!formData.instituicaoId}
        >
          <option value="">Sistema atribuirá automaticamente</option>
          {grupos.map(g => (
            <option key={g.id} value={g.id}>
              {g.nome} ({g.faixaEtariaMin}-{g.faixaEtariaMax} anos)
            </option>
          ))}
        </select>
        <p className="form-hint">
          Se não selecionar, o sistema atribuirá baseado na idade da criança
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <div className="form-group">
        <label className="form-label">
          <Baby size={18} />
          Nome Completo da Criança *
        </label>
        <input
          type="text"
          value={formData.crianca.nome}
          onChange={(e) => handleChange('crianca', 'nome', e.target.value)}
          className="form-input"
          placeholder="Nome completo da criança"
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          <Calendar size={18} />
          Data de Nascimento *
        </label>
        <input
          type="date"
          value={formData.crianca.dataNascimento}
          onChange={(e) => handleChange('crianca', 'dataNascimento', e.target.value)}
          className="form-input"
        />
      </div>

      <div className="form-group">
  <label className="form-label">
    Género
  </label>
  <select
    value={formData.crianca.genero}
    onChange={(e) => handleChange('crianca', 'genero', e.target.value)}
    className="form-select"
  >
    <option value="">Selecione</option>
    <option value="Masculino">Masculino</option>
    <option value="Feminino">Feminino</option>
  </select>
</div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <div className="form-group">
        <label className="form-label">
          <User size={18} />
          Nome do Responsável *
        </label>
        <input
          type="text"
          value={formData.responsavel.nome}
          onChange={(e) => handleChange('responsavel', 'nome', e.target.value)}
          className="form-input"
          placeholder="Nome completo do responsável"
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          <Mail size={18} />
          Email *
        </label>
        <input
          type="email"
          value={formData.responsavel.email}
          onChange={(e) => handleChange('responsavel', 'email', e.target.value)}
          className="form-input"
          placeholder="email@exemplo.com"
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          <Phone size={18} />
          Telefone *
        </label>
        <input
          type="tel"
          value={formData.responsavel.telefone}
          onChange={(e) => handleChange('responsavel', 'telefone', e.target.value)}
          className="form-input"
          placeholder="+258 84 123 4567"
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          Parentesco
        </label>
        <select
          value={formData.responsavel.parentesco}
          onChange={(e) => handleChange('responsavel', 'parentesco', e.target.value)}
          className="form-select"
        >
          <option value="Encarregado">Encarregado</option>
          <option value="Pai">Pai</option>
          <option value="Mãe">Mãe</option>
          <option value="Avô/Avó">Avô/Avó</option>
          <option value="Tio/Tia">Tio/Tia</option>
          <option value="Outro">Outro</option>
        </select>
      </div>
    </div>
  );

  const renderStep4 = () => {
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="step-content">
      <div className="upload-zone" onClick={handleUploadClick} style={{ cursor: 'pointer' }}>
        <Upload size={48} />
        <span className="upload-label">Clique para selecionar ficheiros</span>
        <p className="upload-hint">PDF, JPG, PNG (máx. 5MB cada)</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {documentos.length > 0 && (
        <div className="files-list">
          <p className="files-title">Ficheiros selecionados:</p>
          {documentos.map((file, index) => (
            <div key={index} className="file-item">
              <div className="file-info">
                <FileText size={20} />
                <span>{file.name}</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="file-remove"
              >
                Remover
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="info-box">
        <strong>Documentos recomendados:</strong>
        <ul>
          <li>Certidão de nascimento da criança</li>
          <li>BI/Passaporte do responsável</li>
          <li>Comprovativo de residência (opcional)</li>
        </ul>
      </div>
    </div>
  );
};
  const renderStep5 = () => (
    <div className="success-content">
      <div className="success-icon">
        <CheckCircle size={64} />
      </div>
      <h2>Pré-inscrição Enviada com Sucesso!</h2>
      <p>Receberá um email com as instruções para pagamento e próximos passos.</p>
      <div className="success-info">
        <strong>Próximos Passos:</strong>
        <ol>
          <li>Verifique seu email (incluindo spam)</li>
          <li>Efetue o pagamento da inscrição</li>
          <li>Aguarde aprovação da instituição</li>
        </ol>
      </div>
      <button onClick={() => window.location.reload()} className="btn btn-primary">
        Nova Inscrição
      </button>
    </div>
  );

  const steps = [
    { number: 1, title: 'Instituição', icon: Building2 },
    { number: 2, title: 'Criança', icon: Baby },
    { number: 3, title: 'Responsável', icon: User },
    { number: 4, title: 'Documentos', icon: Upload }
  ];

  return (
    <div className="preinscricao-page">
      {/* Header */}
      <div className="page-header">
        <div className="container">
          <button onClick={() => window.location.href = '/'} className="btn-back">
            <Home size={20} />
            Voltar ao Início
          </button>
          <h1>Pré-inscrição Creche</h1>
          <p>Preencha os dados para inscrever sua criança</p>
        </div>
      </div>

      <div className="container">
        {/* Progress Steps */}
        {!success && (
          <div className="progress-steps">
            {steps.map((s, idx) => (
              <React.Fragment key={s.number}>
                <div className={`step-item ${step >= s.number ? 'active' : ''}`}>
                  <div className="step-circle">
                    <s.icon size={24} />
                  </div>
                  <span className="step-label">{s.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`step-line ${step > s.number ? 'active' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Form Card */}
        <div className="form-card">
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}

          {/* Navigation */}
          {!success && (
            <div className="form-actions">
              <button
                onClick={prevStep}
                disabled={step === 1}
                className="btn btn-secondary"
              >
                <ArrowLeft size={20} />
                Voltar
              </button>

              {step < 4 ? (
                <button onClick={nextStep} className="btn btn-primary">
                  Continuar
                  <ArrowRight size={20} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn btn-success"
                >
                  {loading ? 'Enviando...' : 'Finalizar Inscrição'}
                  <CheckCircle size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="page-footer">
          <p>Tem dúvidas? Entre em contacto através do email da instituição</p>
        </div>
      </div>
    </div>
  );
}